import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdCalendarToday, MdPerson, MdFilterList, MdVisibility, MdPayment } from 'react-icons/md';
import { recibosAPI } from '../services/api';

const ReceiptsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.primary ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.primaryDark : props.theme.colors.background};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const FilterSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  width: 100%;
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  flex: 1;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const FilterInput = styled.input`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ClearFiltersButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: transparent;
  color: ${props => props.theme.colors.textSecondary};
  border: none;
  font-size: ${props => props.theme.fontSizes.xs};
  text-decoration: underline;
  cursor: pointer;
  align-self: flex-end;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ReceiptsListSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const SectionHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const MethodIcon = styled.span`
  font-size: ${props => props.theme.fontSizes.lg};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'pagado') return props.theme.colors.success + '20';
    if (status === 'vencido') return props.theme.colors.danger + '20';
    if (status === 'parcial') return props.theme.colors.info + '20';
    if (status === 'cancelado') return props.theme.colors.textSecondary + '20';
    if (status === 'reembolsado') return '#FF5722' + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'pagado') return props.theme.colors.success;
    if (status === 'vencido') return props.theme.colors.danger;
    if (status === 'parcial') return props.theme.colors.info;
    if (status === 'cancelado') return props.theme.colors.textSecondary;
    if (status === 'reembolsado') return '#FF5722';
    return props.theme.colors.warning;
  }};
  text-decoration: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'cancelado' || status === 'reembolsado') return 'line-through';
    return 'none';
  }};
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.secondary ? props.theme.colors.success : props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.secondary ? '#388E3C' : props.theme.colors.primaryDark};
  }

  &:disabled {
    background-color: ${props => props.theme.colors.textLight};
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const PageButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Receipts = () => {
  const navigate = useNavigate();
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recibosPerPage = 10;
  
  // Estados de filtros
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);

  useEffect(() => {
    loadRecibos();
  }, []);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroEstado, filtroCliente, filtroFechaInicio, filtroFechaFin]);

  const loadRecibos = async () => {
    try {
      setLoading(true);
      const response = await recibosAPI.getAll();
      const recibosData = response.data.data || [];
      
      // Debug: Ver qué estados vienen de la BD
      console.log('Estados de recibos:', recibosData.map(r => ({ 
        id: r.id, 
        numero: r.numero_recibo,
        estado: r.estado 
      })));
      
      setRecibos(recibosData);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar recibos');
      console.error('Error al cargar recibos:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const formatEstado = (estado) => {
    if (!estado) return '';
    return estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
  };
  
  const getPaymentIcon = (method) => {
    if (method && method.includes('Visa')) return '💳';
    if (method && method.includes('Mastercard')) return '💳';
    if (method && method.includes('PayPal')) return '🏦';
    if (method && method.includes('Transferencia')) return '🏦';
    return '💰';
  };

  // Aplicar filtros
  const recibosFiltrados = recibos.filter(recibo => {
    // Filtro por estado (case-insensitive)
    if (filtroEstado !== 'todos') {
      const estadoRecibo = (recibo.estado || '').toLowerCase();
      const estadoFiltro = filtroEstado.toLowerCase();
      
      // Debug
      if (recibo.id === recibos[0]?.id) {
        console.log('Filtro estado debug:', {
          filtroEstado,
          estadoFiltro,
          'recibo.estado': recibo.estado,
          estadoRecibo
        });
      }
      
      if (estadoFiltro === 'pendiente' && estadoRecibo !== 'pendiente') return false;
      if (estadoFiltro === 'pagado' && estadoRecibo !== 'pagado') return false;
      if (estadoFiltro === 'vencido' && estadoRecibo !== 'vencido') return false;
      if (estadoFiltro === 'parcial' && estadoRecibo !== 'parcial') return false;
    }
    
    // Filtro por cliente
    if (filtroCliente && !recibo.cliente_nombre.toLowerCase().includes(filtroCliente.toLowerCase())) {
      return false;
    }
    
    // Filtro por fecha
    if (filtroFechaInicio) {
      const fechaRecibo = new Date(recibo.fecha_emision);
      const fechaInicio = new Date(filtroFechaInicio);
      if (fechaRecibo < fechaInicio) return false;
    }
    
    if (filtroFechaFin) {
      const fechaRecibo = new Date(recibo.fecha_emision);
      const fechaFin = new Date(filtroFechaFin);
      if (fechaRecibo > fechaFin) return false;
    }
    
    return true;
  });

  const limpiarFiltros = () => {
    setFiltroEstado('todos');
    setFiltroCliente('');
    setFiltroFechaInicio('');
    setFiltroFechaFin('');
    setCurrentPage(1);
  };

  // Calcular paginación sobre recibos filtrados
  const indexOfLastRecibo = currentPage * recibosPerPage;
  const indexOfFirstRecibo = indexOfLastRecibo - recibosPerPage;
  const currentRecibos = recibosFiltrados.slice(indexOfFirstRecibo, indexOfLastRecibo);
  const totalPages = Math.ceil(recibosFiltrados.length / recibosPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <ReceiptsContainer>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando recibos...</p>
        </div>
      </ReceiptsContainer>
    );
  }

  if (error) {
    return (
      <ReceiptsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#E57373' }}>
          <p>Error: {error}</p>
          <button onClick={loadRecibos}>Reintentar</button>
        </div>
      </ReceiptsContainer>
    );
  }

  return (
    <ReceiptsContainer>
      <PageHeader>
        <Title>Recibos y Facturas</Title>
        <ActionButtons>
          <Button primary onClick={() => navigate('/registrar-pago')}>
            <MdAdd />
            Registrar Pago
          </Button>
          <Button onClick={() => navigate('/recibos/nuevo')}>
            <MdAdd />
            Nuevo Recibo
          </Button>
        </ActionButtons>
      </PageHeader>

      <FilterSection>
        <FilterButton 
          active={showFiltros}
          onClick={() => setShowFiltros(!showFiltros)}
        >
          <MdFilterList />
          {showFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </FilterButton>
        
        {showFiltros && (
          <FilterControls>
            <FilterGroup>
              <FilterLabel>Estado</FilterLabel>
              <FilterSelect 
                value={filtroEstado} 
                onChange={(e) => {
                  setFiltroEstado(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="todos">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="pagado">Pagado</option>
                <option value="parcial">Parcial</option>
                <option value="vencido">Vencido</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Cliente</FilterLabel>
              <FilterInput 
                type="text"
                placeholder="Buscar por nombre..."
                value={filtroCliente}
                onChange={(e) => {
                  setFiltroCliente(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Fecha Inicio</FilterLabel>
              <FilterInput 
                type="date"
                value={filtroFechaInicio}
                onChange={(e) => {
                  setFiltroFechaInicio(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Fecha Fin</FilterLabel>
              <FilterInput 
                type="date"
                value={filtroFechaFin}
                onChange={(e) => {
                  setFiltroFechaFin(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </FilterGroup>
            
            <ClearFiltersButton onClick={limpiarFiltros}>
              Limpiar filtros
            </ClearFiltersButton>
          </FilterControls>
        )}
      </FilterSection>

      <ReceiptsListSection>
        <SectionHeader>
          <SectionTitle>Lista de Recibos</SectionTitle>
          <SectionSubtitle>
            {recibosFiltrados.length} recibo{recibosFiltrados.length !== 1 ? 's' : ''} 
            {recibosFiltrados.length !== recibos.length && ` (filtrados de ${recibos.length} totales)`}
          </SectionSubtitle>
        </SectionHeader>
        
        <Table>
          <Thead>
            <tr>
              <Th>Cliente</Th>
              <Th>Fecha</Th>
              <Th>Monto Total</Th>
              <Th>Monto Pendiente</Th>
              <Th>No. de Recibo</Th>
              <Th>Estatus</Th>
              <Th>Acciones</Th>
            </tr>
          </Thead>
          <tbody>
            {currentRecibos.length > 0 ? (
              currentRecibos.map(recibo => (
                <tr key={recibo.id}>
                  <Td>{recibo.cliente_nombre}</Td>
                  <Td>{new Date(recibo.fecha_emision).toLocaleDateString('es-MX')}</Td>
                  <Td>${(recibo.monto_total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td style={{ 
                    color: parseFloat(recibo.saldo_pendiente || 0) > 0 ? '#E57373' : '#4CAF50',
                    fontWeight: '600'
                  }}>
                    ${(recibo.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </Td>
                  <Td>
                    <PaymentMethod>
                      <MethodIcon>📄</MethodIcon>
                      Recibo #{recibo.numero_recibo}
                    </PaymentMethod>
                  </Td>
                  <Td>
                    <StatusBadge status={formatEstado(recibo.estado)}>{formatEstado(recibo.estado)}</StatusBadge>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <ActionButton onClick={() => navigate(`/recibos/${recibo.id}`)}>
                        <MdVisibility />
                        Ver
                      </ActionButton>
                      {recibo.estado !== 'pagado' && recibo.estado !== 'cancelado' && parseFloat(recibo.saldo_pendiente || 0) > 0 && (
                        <ActionButton 
                          secondary 
                          onClick={() => navigate('/registrar-pago', { state: { reciboId: recibo.id } })}
                          title="Registrar pago"
                        >
                          <MdPayment />
                          Pagar
                        </ActionButton>
                      )}
  
                    </ActionsCell>
                  </Td>
              </tr>
            ))
            ) : (
              <tr>
                <Td colSpan="7" style={{ textAlign: 'center' }}>No hay recibos disponibles</Td>
              </tr>
            )}
          </tbody>
        </Table>
        
        {totalPages > 1 && (
          <Pagination>
            <PageButton 
              onClick={handlePrevious} 
              disabled={currentPage === 1}
            >
              Previous
            </PageButton>
            
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} style={{ padding: '0 8px' }}>...</span>
              ) : (
                <PageButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              )
            ))}
            
            <PageButton 
              onClick={handleNext} 
              disabled={currentPage === totalPages}
            >
              Next
            </PageButton>
          </Pagination>
        )}
      </ReceiptsListSection>
    </ReceiptsContainer>
  );
};

export default Receipts;
